/**
 * ``useCheckout`` — create one order for the whole cart, atomically and idempotently (ТЗ §5,§10).
 *
 * The idempotency key is stable per *cart composition*: it is generated once and only changes
 * when the selected items change (``cartSignature``) — never when the requested currency spend
 * changes. So a retried/double-tapped «Подтвердить заказ» (e.g. after a lost response) reuses
 * the key and the server returns the already-created order instead of creating a second one.
 * The requested spend and comment are sent so the server prices and debits the exact same
 * thing the checkout screen previewed.
 */
import { ApiError } from "@shared/api";
import type { OrderDetail } from "@entities/order";
import { newIdempotencyKey } from "@features/product-order";
import { useCallback, useMemo, useRef, useState } from "react";

import { createCartOrder } from "./api";
import { cartSignature, toItemsPayload, type CartItems } from "./model";
import type { CartSpend } from "./useCartQuote";

export interface CheckoutInput extends CartSpend {
  readonly userComment?: string;
  /** Customer requests a payment receipt at checkout (ТЗ №2доп §16-18). */
  readonly needsReceipt?: boolean;
}

export interface CheckoutState {
  readonly error: ApiError | null;
  readonly isSubmitting: boolean;
  /** Submit the order; resolves with the created order or rejects with {@link ApiError}. */
  readonly submit: () => Promise<OrderDetail>;
}

export function useCheckout(
  items: CartItems,
  input: CheckoutInput = {},
): CheckoutState {
  const [error, setError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const starGlitterSpend = input.starGlitterSpend ?? 0;
  const primogemSpend = input.primogemSpend ?? 0;
  const userComment = input.userComment ?? "";
  const needsReceipt = input.needsReceipt ?? false;

  // One idempotency key per cart composition; regenerated only when the selection changes —
  // NOT when the requested spend changes — so a retry after a lost response reuses the key and
  // the server returns the already-created order rather than creating a duplicate.
  const signature = cartSignature(items);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const idempotencyKey = useMemo(() => newIdempotencyKey(), [signature]);

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const keyRef = useRef(idempotencyKey);
  keyRef.current = idempotencyKey;
  const spendRef = useRef({
    starGlitterSpend,
    primogemSpend,
    userComment,
    needsReceipt,
  });
  spendRef.current = {
    starGlitterSpend,
    primogemSpend,
    userComment,
    needsReceipt,
  };

  const submit = useCallback(async (): Promise<OrderDetail> => {
    const current = itemsRef.current;
    if (current.size === 0) {
      throw new ApiError(0, "Cart is empty", null);
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const order = await createCartOrder(
        {
          items: toItemsPayload(current),
          star_glitter_spend: spendRef.current.starGlitterSpend,
          primogem_spend: spendRef.current.primogemSpend,
          user_comment: spendRef.current.userComment,
          needs_receipt: spendRef.current.needsReceipt,
        },
        keyRef.current,
      );
      return order;
    } catch (err: unknown) {
      const apiError =
        err instanceof ApiError ? err : new ApiError(0, "Network error", err);
      setError(apiError);
      throw apiError;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { error, isSubmitting, submit };
}
